import { AdsOrderStatus } from "@/interface/order";
import getHoursDiffFromNow from "@/utils/getHoursDiffFromNow";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { getAdsOrderStatusLabel } from "@/interface/order";
import { OrderPriceNegotiationStatus } from "@/biz/order";

const OrderStatusComponent: React.FC<{
  data: any;
  isOrderDetail?: boolean;
  showAhaPicksQuoteStatus?: boolean;
}> = ({ data, isOrderDetail, showAhaPicksQuoteStatus = false }) => {
  const { t } = useTranslation();
  const { status } = data || {};
  const getStatusStyle = (status: string) => {
    switch (status) {
      case AdsOrderStatus.CollaborationEstablished:
        if (
          data?.priceNegotiationStatus ===
          OrderPriceNegotiationStatus.UnderAdsReview
        ) {
          return { bg: "#FE9A00", text: "#fff" };
        }
        if (
          data?.priceNegotiationStatus ===
          OrderPriceNegotiationStatus.UnderCreatorReview
        ) {
          return { bg: "#FFFBEB", text: "#973C00" };
        }
        // 达人确认价格
        if (
          data?.priceNegotiationStatus ===
          OrderPriceNegotiationStatus.PendingAdsApproval
        ) {
          return { bg: "#F5F3FF", text: "#5D0EC0" };
        }
        return { bg: "#EFF6FF", text: "#193CB8" };
      case AdsOrderStatus.UnderScriptReview:
      case AdsOrderStatus.UnderContentReview:
        return { bg: "#F6339A", text: "#fff" };
      case AdsOrderStatus.PendingProductShipment:
        return { bg: "#FEFCE8", text: "#854D0E" };
      case AdsOrderStatus.CollaborationWithdrawn:
        return { bg: "#F5F5F5", text: "#262626" };
      case AdsOrderStatus.UnderScriptProduction:
      case AdsOrderStatus.UnderContentProduction:
        return { bg: "#ECFEFF", text: "#005F78" };
      case AdsOrderStatus.AdsPendingPublish:
        return { bg: "#ECFDF5", text: "#006045" };
      case AdsOrderStatus.AdsPublished:
        return { bg: "#00BC7D", text: "#fff" };
      case AdsOrderStatus.CollaborationCancelled:
        return { bg: "#292524", text: "#fff" };
      case AdsOrderStatus.PendingProductArrival:
      case AdsOrderStatus.PendingCreatorAddress:
        return { bg: "#F5F3FF", text: "#4C1D95" };
      case AdsOrderStatus.PendingProductionKickoff:
        return { bg: "#FFFBEB", text: "#973C00" };
      case AdsOrderStatus.PendingCreatorAccountInfo:
        return { bg: "#EFF6FF", text: "#193CB8" };
      case AdsOrderStatus.PendingAccountSetup:
        return { bg: "#F5F3FF", text: "#5D0EC0" };
      case AdsOrderStatus.PendingSelection:
        return { bg: "#FFF6ED", text: "#9B2911" };
      case AdsOrderStatus.QuoteRequested:
        return { bg: "#EFF6FF", text: "#193CB8" };
      default:
        return { bg: "#fafafa", text: "#86909C" };
    }
  };

  const { bg, text } = getStatusStyle(status);
  const statusText =
    showAhaPicksQuoteStatus &&
    status === AdsOrderStatus.QuoteRequested
      ? data?.ahaPicksOffer
        ? t("components.orderStatusComponent.openingOfferSentAndAwaitingReply", {
            defaultValue: "Opening offer sent & awaiting reply",
          })
        : t("components.orderStatusComponent.requestedQuoteAndAwaitingReply", {
            defaultValue: "Requested quote & awaiting reply",
          })
      : getAdsOrderStatusLabel(status, data, t);

  const getTooltipText = () => {
    if (status === AdsOrderStatus.PendingAccountSetup) {
      return t("components.orderStatusComponent.pendingAccountSetupTooltip");
    }
    if (status === AdsOrderStatus.PendingCreatorAccountInfo) {
      return t(
        "components.orderStatusComponent.pendingCreatorAccountInfoTooltip",
      );
    }
    if (
      status === AdsOrderStatus.CollaborationEstablished &&
      data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.UnderAdsReview
    ) {
      return t("components.orderStatusComponent.underAdsReviewTooltip");
    }
    if (
      status === AdsOrderStatus.CollaborationEstablished &&
      data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.UnderCreatorReview
    ) {
      return t("components.orderStatusComponent.underCreatorReviewTooltip");
    }
    if (
      status === AdsOrderStatus.CollaborationEstablished &&
      data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.PendingAdsApproval
    ) {
      return t("components.orderStatusComponent.pendingAdsApprovalTooltip");
    }
    if (
      isOrderDetail &&
      status === AdsOrderStatus.QuoteRequested &&
      !!data?.ahaPicksOffer
    ) {
      return t("components.orderStatusComponent.openingOfferSentTooltip");
    }
    return "";
  };

  // 延迟后根据不同状态显示不同的文案
  const getStatusText = () => {
    return (
      <Tooltip
        title={t("components.orderStatusComponent.behindScheduleTooltip")}
      >
        <div
          className="h-6 px-4 flex items-center justify-center text-xs font-medium  leading-none rounded-[999px] whitespace-nowrap"
          style={{ backgroundColor: "#FFF6ED", color: "#9B2911" }}
        >
          {t("components.orderStatusComponent.slightlyBehindSchedule")}
        </div>
      </Tooltip>
    );
  };

  // 如果 data 不存在，返回 null
  if (!data) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 justify-center items-center">
      <div className="flex justify-center">
        {/* 下一操作时间有，且下一操作时间超过后 显示 Late submission 状态 */}
        {/* 不超过一个月（30天）不显示逾期标注, 逾期的时间是负数，所以小于-(30 * 24) */}
        {data.creatorNextOperateDate &&
        getHoursDiffFromNow(data.creatorNextOperateDate) <= -(30 * 24) ? (
          getStatusText()
        ) : (
          <Tooltip title={getTooltipText()}>
            <div
              className="h-[22px] md:h-6 px-[6px] md:px-4 flex items-center justify-center text-[12px] md:text-xs font-medium  leading-none rounded-[999px] whitespace-nowrap"
              style={{ backgroundColor: bg, color: text }}
            >
              {statusText}
            </div>
          </Tooltip>
        )}
      </div>
      {status === AdsOrderStatus.QuoteRequested &&
        !isOrderDetail &&
        !showAhaPicksQuoteStatus && (
          <div className="justify-start text-[#79716B] text-xs font-normal font-['DM_Sans'] leading-4">
            {!!data?.ahaPicksOffer
              ? t("components.orderStatusComponent.openingOfferSent", {
                  defaultValue: "Your opening offer sent",
                })
              : t("components.orderStatusComponent.youveRequestedQuote", {
                  defaultValue: "You've requested quote",
                })}
          </div>
        )}
    </div>
  );
};

export default OrderStatusComponent;
